// CRM-specific ru catalog (flat keys, matching `<scope>.<domain>.<key>`).
// Namespace is `crm` — access via `t('crm.nav.dashboard')` etc.
//
// Phase 1 keeps only the strings rendered by AppLayout / Sidebar / Topbar.
// Page-level strings land here as pages are built (Week 4+).

import type { Namespace } from '@govard/i18n-core';

export const crmRu: Namespace = {
  'common.cancel': 'Отмена',
  // Sidebar navigation — Simple section (5 canonical items + Settings).
  'nav.dashboard': 'Дашборд',
  'nav.deals': 'Сделки',
  'nav.contacts': 'Контакты',
  'nav.templates': 'Шаблоны',
  'nav.hot_leads': 'Горячие лиды',
  'nav.memory': 'Память',
  'nav.run_center': 'Run Center',
  'nav.scenario_editor': 'Scenario Editor',
  'nav.prompt_compiler': 'Prompt Compiler',
  'nav.knowledge_base': 'Knowledge Base',
  'nav.files': 'Files',
  'nav.agents': 'Agents',
  'nav.opencode': 'OpenCode',
  'nav.settings': 'Настройки',

  // Pro-mode collapsible section (empty in Phase 1).
  'nav.pro_section': 'Расширенные (Pro)',
  'nav.pro_empty': 'Здесь появятся инструменты Pro-режима.',

  // Sidebar groups / branding.
  'sidebar.simple_section': 'Основное',
  'sidebar.workspace': 'Рабочее пространство',
  'sidebar.brand': 'Govard CRM',

  // Topbar.
  'topbar.user_menu': 'Меню пользователя',
  'topbar.logout': 'Выйти',
  'topbar.toggle_context': 'Контекстная панель',
  'topbar.operator_status': 'Статус операторского контура',
  'topbar.system_agent': 'GovardOS Agent',
  'topbar.llm_gate': 'LLM Gate',
  'topbar.scope_label': 'Scope',
  'topbar.status_online': 'Онлайн',
  'topbar.logout_error': 'Не удалось выйти. Попробуйте ещё раз.',
  'topbar.workspace_fallback': 'Без рабочего пространства',

  // Right sidebar.
  'right_sidebar.title': 'Контекстная панель',
  'right_sidebar.close': 'Закрыть контекстную панель',
  'right_sidebar.description': 'Shell-панель для operator-контекста и быстрой диагностики.',
  'right_sidebar.system_status': 'System Status',
  'right_sidebar.block_status': 'Статус',
  'right_sidebar.status_online': 'Онлайн',
  'right_sidebar.block_scope': 'Scope',
  'right_sidebar.scope_workspace': 'Текущий workspace',
  'right_sidebar.recent_activity': 'Recent Activity',
  'right_sidebar.activity_visual_contract': 'Visual contract закреплен',
  'right_sidebar.activity_now': 'только что',
  'right_sidebar.activity_next_slice': 'Следующий срез: shell/deals parity',
  'right_sidebar.activity_next': 'следующий шаг',

  // Settings Week 5.
  'settings.title': 'Настройки',
  'settings.subtitle': 'Профиль, рабочее пространство, AI и флаги функций для текущего workspace.',
  'settings.tabs_aria': 'Разделы настроек',
  'settings.tabs.profile': 'Профиль',
  'settings.tabs.workspace': 'Рабочее пространство',
  'settings.tabs.ai': 'AI',
  'settings.tabs.flags': 'Feature flags',
  'settings.loading': 'Загружаем настройки рабочего пространства...',
  'settings.profile_stub': 'Личные настройки профиля и команды появятся в следующих шагах Week 5.',
  'settings.workspace_stub': 'Здесь будут параметры рабочего пространства и участники команды.',
  'settings.ai_stub': 'Настройки моделей и лимитов AI будут доступны после интеграции chat flow.',
  'settings.flags_title': 'Флаги функций',
  'settings.flags_description':
    'Включайте продвинутые режимы только когда готовы к расширенному интерфейсу.',
  'settings.pro_mode_title': 'Pro mode',
  'settings.pro_mode_description': 'Показывает расширенную секцию Advanced (Pro) в боковом меню.',
  'settings.enable_pro_mode': 'Включить Pro mode',
  'settings.disable_pro_mode': 'Выключить Pro mode',
  'settings.confirm_title': 'Включить продвинутый режим?',
  'settings.confirm_body':
    'Появится Scenario Engine, Run Center, Memory Browser. Это для тех, кто хочет тонко настраивать AI-поведение. Основные функции работают и так.',
  'settings.confirm_submit': 'Включить Pro mode',

  // Deals Week 4.
  'deals.title': 'Сделки',
  'deals.subtitle':
    'Канбан по стадиям. Перетаскивайте карточки или используйте быстрый переход в следующую стадию.',
  'deals.loading': 'Загрузка сделок...',
  'deals.load_error': 'Не удалось загрузить сделки',
  'deals.retry': 'Повторить',
  'deals.empty_stage': 'В этой стадии пока нет сделок.',
  'deals.move_to_stage': 'Переместить в {stage}',
  'deals.final_stage': 'Финальная стадия',
  'deals.open_hot_leads': 'Горячие лиды',
  'deals.summary.total': 'Всего сделок',
  'deals.summary.active': 'В работе',
  'deals.summary.attention': 'Нужны действия',
  'deals.next_step': 'Следующий шаг',
  'deals.next_step_missing': 'Не запланирован',
  'deals.stage.New': 'Новая',
  'deals.stage.Contacted': 'Контакт установлен',
  'deals.stage.Negotiation': 'Переговоры',
  'deals.stage.Won': 'Успешно',
  'deals.stage.Lost': 'Потеряна',

  // Contacts Week 5.
  'contacts.title': 'Контакты',
  'contacts.subtitle': 'Поиск по базе клиентов, быстрый просмотр и защита от дублей при создании.',
  'contacts.list_title': 'База контактов',
  'contacts.list_description': 'Ищите по имени, email или телефону и открывайте карточку контакта.',
  'contacts.search_label': 'Поиск контактов',
  'contacts.search_submit': 'Найти',
  'contacts.loading': 'Загрузка контактов...',
  'contacts.no_channel': 'Контакты не указаны',
  'contacts.create_title': 'Новый контакт',
  'contacts.create_description':
    'Добавьте клиента вручную. Если email уже есть в базе, покажем существующую карточку.',
  'contacts.create_submit': 'Добавить контакт',
  'contacts.form.first_name': 'Имя',
  'contacts.form.last_name': 'Фамилия',
  'contacts.form.email': 'Email',
  'contacts.form.phone': 'Телефон',
  'contacts.duplicate_banner': 'Такой email уже существует',
  'contacts.open_contact': 'Открыть контакт',
  'contacts.summary.total': 'Всего контактов',
  'contacts.summary.with_deals': 'Со сделками',
  'contacts.summary.with_email': 'С email',
  'contacts.back_to_contacts': 'К контактам',
  'contacts.detail.deals': 'Сделки',
  'contacts.detail.notes': 'Заметки',
  'contacts.detail.interactions': 'Последние касания',
  'contacts.detail.no_interactions': 'Контактных событий пока нет.',
  'contacts.detail.source': 'Источник',
  'contacts.detail.source_missing': 'Не указан',

  // Templates Week 5.
  'templates.title': 'Шаблоны',
  'templates.subtitle': 'Каталог готовых шаблонов для писем, SMS и in-app сообщений.',
  'templates.subtitle_unit': 'шаблонов',
  'templates.list_title': 'Каталог шаблонов',
  'templates.editor_title': 'Редактор шаблона',
  'templates.editor_empty': 'Выберите шаблон в таблице, чтобы изменить текст и переменные.',
  'templates.new_template': '6 шаблонов из seed доступны для редактирования.',
  'templates.new': 'Новый шаблон',
  'templates.name': 'Название',
  'templates.channel_label': 'Канал',
  'templates.search_label': 'Поиск шаблонов',
  'templates.search_placeholder': 'Поиск по названию, теме или тексту',
  'templates.refresh': 'Обновить шаблоны',
  'templates.reset_filters': 'Сбросить',
  'templates.new_body': 'Текст нового шаблона',
  'templates.create_submit': 'Создать шаблон',
  'templates.create_description':
    'Создайте новый шаблон для email, SMS или сообщений в приложении.',
  'templates.loading': 'Загрузка шаблонов...',
  'templates.load_error': 'Не удалось загрузить шаблоны',
  'templates.load_error_description': 'Проверьте соединение и попробуйте повторить запрос.',
  'templates.retry': 'Повторить',
  'templates.empty': 'Шаблонов пока нет. Создайте первый шаблон для быстрых сообщений.',
  'templates.empty_filtered': 'Нет шаблонов по текущим фильтрам.',
  'templates.save': 'Сохранить',
  'templates.open': 'Открыть',
  'templates.subject': 'Тема',
  'templates.body': 'Текст',
  'templates.no_preview': 'Без темы или текста',
  'templates.no_variables': 'Без переменных',
  'templates.variables': 'Переменные',
  'templates.usage_count': 'Использований',
  'templates.channel_filter_label': 'Канал:',
  'templates.name_placeholder': 'Название шаблона',
  'templates.channel.email': 'Email',
  'templates.channel.sms': 'SMS',
  'templates.channel.in_app': 'В приложении',
  'templates.filter.all': 'Все',
  'templates.summary.total': 'Всего шаблонов',
  'templates.summary.email': 'Email',
  'templates.summary.sms': 'SMS',
  'templates.summary.in_app': 'В приложении',
  'templates.table.template': 'Шаблон',
  'templates.table.action': 'Действие',
  'templates.editor.usage_count': 'Использований {count}',
  'templates.editor.variables_count_one': '{count} переменная',
  'templates.editor.variables_count_few': '{count} переменные',
  'templates.editor.variables_count_many': '{count} переменных',

  // Hot leads Week 5.
  'hot_leads.title': 'Горячие лиды',
  'hot_leads.subtitle': 'Сделки, которым нужно внимание прямо сейчас.',
  'hot_leads.table_title': 'Приоритетные сделки',
  'hot_leads.table_description':
    'Эвристика на основе активности, ближайших действий и суммы сделки.',
  'hot_leads.table_header.deal': 'Сделка',
  'hot_leads.table_header.contact': 'Контакт',
  'hot_leads.table_header.stage': 'Стадия',
  'hot_leads.table_header.reason': 'Причина',
  'hot_leads.table_header.action': 'Действие',
  'hot_leads.loading': 'Считаем горячие лиды...',
  'hot_leads.no_contact': 'Без контакта',
  'hot_leads.filter.all': 'Все',
  'hot_leads.filter.stale': 'без активности',
  'hot_leads.filter.due': 'ближайшие действия',
  'hot_leads.filter.high_value': 'высокая сумма',
  'hot_leads.reason.stale': 'без активности 4 дня',
  'hot_leads.reason.due': 'действие скоро',
  'hot_leads.reason.high_value': 'высокая сумма',
  'hot_leads.open_ai': 'Открыть в AI-чате',

  // Approvals Week 5.
  'approvals.approve': 'Подтвердить',
  'approvals.reject': 'Отклонить',
  'approvals.approved': 'Подтверждено',
  'approvals.rejected': 'Отклонено',
  'approvals.expires_at': 'Истекает:',

  // Deal card Week 6.
  'deal_card.loading': 'Загрузка сделки...',
  'deal_card.retry': 'Повторить',
  'deal_card.error_title': 'Не удалось загрузить сделку',
  'deal_card.error_retry': 'Попробуйте обновить страницу.',
  'deal_card.back_to_deals': 'К сделкам',
  'deal_card.sections_label': 'Разделы сделки',
  'deal_card.tabs.details': 'Детали',
  'deal_card.tabs.activity': 'Активность',
  'deal_card.tabs.notes': 'Заметки',
  'deal_card.tabs.chat': 'Чат',
  'deal_card.summary.amount': 'Сумма',
  'deal_card.summary.contact': 'Контакт',
  'deal_card.summary.source': 'Источник',
  'deal_card.summary.activity_count': 'Активности',
  'deal_card.summary.next_step': 'Следующий шаг',
  'deal_card.summary.owner': 'Ответственный',
  'deal_card.activity_count': '{count} активности',
  'deal_card.details_title': 'Детали сделки',
  'deal_card.details.contact': 'Контакт:',
  'deal_card.details.contact_missing': 'Контакт не указан',
  'deal_card.details.source': 'Источник:',
  'deal_card.details.source_missing': 'Не указан',
  'deal_card.details.next_activity': 'Следующая активность:',
  'deal_card.details.next_activity_missing': 'Не запланирована',
  'deal_card.form.title': 'Название сделки',
  'deal_card.form.amount': 'Сумма',
  'deal_card.form.source': 'Источник',
  'deal_card.form.next_activity': 'Следующая активность',
  'deal_card.details_save': 'Сохранить детали',
  'deal_card.activity_title': 'Последние активности',
  'deal_card.notes_title': 'Заметки',
  'deal_card.notes_placeholder': 'Редактор заметок подключим на следующем шаге.',
  'deal_card.notes_empty': 'Заметок пока нет.',
  'deal_card.note_new_label': 'Новая заметка',
  'deal_card.note_placeholder': 'Что важно помнить перед следующим контактом?',
  'deal_card.note_save': 'Сохранить заметку',
  'deal_card.note_subject_fallback': 'Заметка',
  'deal_card.quick_add_note': 'Добавить заметку',
  'deal_card.chat_title': 'Чат',
  'deal_card.chat_desktop_note': 'На desktop AI-чат закреплён в правой колонке.',
  'deal_card.chat_mobile_title': 'AI Chat',
  'deal_card.chat_mobile_description': 'Чат открыт в мобильном focus-режиме для текущей сделки.',
  'deal_card.owner_title': 'Ответственный',
  'deal_card.owner_label': 'Ответственный',
  'deal_card.owner_missing': 'Не назначен',
  'deal_card.close_chat': 'Закрыть',

  // Dashboard Week 7/8 onboarding.
  'dashboard.onboarding.title': 'Настройка CRM',
  'dashboard.onboarding.progress_label': 'Прогресс',
  'dashboard.onboarding.first_deal': 'Первая сделка',
  'dashboard.onboarding.client_contacted': 'Контакт с клиентом',
  'dashboard.onboarding.deal_won': 'Успешная сделка',
  'dashboard.onboarding.dismiss': 'Скрыть',

  // Dashboard Week 7.
  'dashboard.title': 'Mission Control',
  'dashboard.subtitle': 'Welcome back. Here is what is happening across your systems.',
  'dashboard.loading': 'Обновляем ключевые метрики...',
  'dashboard.kpi.projects': 'Projects',
  'dashboard.kpi.projects_hint': 'Portfolio entities',
  'dashboard.kpi.active': 'Active',
  'dashboard.kpi.active_hint': 'Open pipeline deals',
  'dashboard.kpi.tasks_done': 'Tasks done',
  'dashboard.kpi.tasks_done_hint': 'Scheduled vs total',
  'dashboard.kpi.sessions': 'Sessions',
  'dashboard.kpi.sessions_hint': 'Running scenario runs',
  'dashboard.kpi.success_rate': 'Success rate',
  'dashboard.kpi.success_rate_hint': 'Completed scenario runs',
  'dashboard.kpi.total_cost': 'Total cost',
  'dashboard.kpi.total_cost_hint': 'Template usage + ops load',
  'dashboard.workforce.active': '{count} active',

  // Chat Week 6.
  'chat.title': 'AI Chat',
  'chat.ready': 'Готов',
  'chat.context_hint': 'Ассистент видит текущую сделку, контакт, активность и может подготовить следующий шаг.',
  'chat.suggestions_title': 'Быстрые сценарии',
  'chat.suggestion_next_step': 'Предложить следующий шаг',
  'chat.suggestion_next_step_prompt': 'Проанализируй текущую сделку {dealId} и предложи один лучший следующий шаг с коротким обоснованием.',
  'chat.suggestion_risks': 'Найти риск потери сделки',
  'chat.suggestion_risks_prompt': 'Проанализируй текущую сделку {dealId} и назови главный риск потери сделки. Дай конкретное действие для снижения риска.',
  'chat.placeholder': 'Спросите ассистента о сделке...',
  'chat.send': 'Отправить',
  'chat.usage': 'Стоимость последнего ответа: {cost} ₽',
  'chat.quick_follow_up': 'Написать follow-up',
  'chat.quick_follow_up_prompt': 'Подготовь follow-up письмо для текущей сделки {dealId} через tool draft_email. Тон: warm. Верни черновик и не вызывай send_email.',

  // Memory Browser Week 7.
  'memory.title': 'Memory Browser',
  'memory.subtitle': 'Просмотр памяти агента по уровням L1/L2/L3 и семантический поиск L3.',
  'memory.browser_title': 'Память рабочего пространства',
  'memory.browser_description':
    'L1 — буфер сессии, L2 — эпизодическая память, L3 — векторные знания.',
  'memory.tabs_aria': 'Уровни памяти',
  'memory.search_label': 'Семантический поиск',
  'memory.search_placeholder': 'Например: лучшее время для связи',
  'memory.search_submit': 'Найти',
  'memory.search_loading': 'Ищем по памяти L3...',
  'memory.search_empty': 'По запросу «{query}» ничего не найдено.',
  'memory.score': 'Score: {score}',
  'memory.importance': 'Важность: {value}',
  'memory.empty': 'Записей пока нет.',

  // Dashboard widgets Week 8.
  'dashboard.page.title': 'Mission Control',
  'dashboard.page.subtitle': 'Welcome back. Here is what is happening across your systems.',
  'dashboard.presets.ceo': 'CEO',
  'dashboard.presets.pm': 'PM',
  'dashboard.presets.dev': 'Dev',
  'dashboard.presets.creative': 'Creative',
  'dashboard.presets.flatmedia': 'Flatmedia',
  'dashboard.stats.projects': 'Сделки',
  'dashboard.stats.active': 'В работе',
  'dashboard.stats.tasks_done': 'Горячие лиды',
  'dashboard.stats.sessions': 'Активные сценарии',
  'dashboard.stats.sessions_suffix': 'активно',
  'dashboard.stats.success_rate': 'Успешные сделки',
  'dashboard.stats.total_cost': 'Ожидают подтверждения',
  'dashboard.focus.title': 'Фокус на сегодня',
  'dashboard.focus.view_all': 'Показать всё',
  'dashboard.focus.empty': 'Приоритетных задач нет. Всё под контролем.',
  'dashboard.focus.no_project': 'Без проекта',
  'dashboard.workforce.title': 'Сигналы AI',
  'dashboard.workforce.active_count': '{count} активно',
  'dashboard.workforce.empty': 'Активных сигналов пока нет',
  'dashboard.workforce.signal.scenario_run': 'Активный сценарий',
  'dashboard.workforce.signal.pending_approval': 'Ожидает подтверждения',
  'dashboard.workforce.type.opencode': 'opencode',
  'dashboard.workforce.type.antigravity': 'antigravity',
  'dashboard.workforce.type.agent': 'агент',
  'dashboard.burndown.title': 'Динамика сделок',
  'dashboard.burndown.empty': 'История по динамике пока не накопилась.',
  'dashboard.burndown.ideal': 'План',
  'dashboard.burndown.remaining': 'Осталось',
  'dashboard.pipelines.title': 'Этапы сделок',
  'dashboard.pipelines.empty': 'Активных этапов пока нет',
  'dashboard.activity.title': 'Последняя активность',
  'dashboard.activity.events_count': '{count} событий',
  'dashboard.activity.empty': 'Свежей активности пока нет',
  'dashboard.activity.fallback_title': 'Активность',
  'dashboard.calendar.title': 'Ближайшие действия',
  'dashboard.calendar.empty': 'Ближайших действий пока нет',
  'dashboard.calendar.overdue': 'просрочено',
  'dashboard.calendar.today': 'сегодня',
  'dashboard.calendar.tomorrow': 'завтра',
  'dashboard.calendar.in_days': 'через {count}д',
  'dashboard.actions.title': 'Быстрые действия',
  'dashboard.actions.new_project.label': 'Новый проект',
  'dashboard.actions.new_project.subtitle': 'Создать рабочее пространство',
  'dashboard.actions.summon_agent.label': 'Позвать AI',
  'dashboard.actions.summon_agent.subtitle': 'Быстрый запуск помощника',
  'dashboard.actions.new_pipeline.label': 'Новая воронка',
  'dashboard.actions.new_pipeline.subtitle': 'Настроить этапы работы',
  'dashboard.actions.analytics.label': 'Аналитика',
  'dashboard.actions.analytics.subtitle': 'Посмотреть метрики',
  'dashboard.actions.settings.label': 'Настройки',
  'dashboard.actions.settings.subtitle': 'Параметры системы',
  'dashboard.notifications.title': 'Уведомления',
  'dashboard.notifications.mark_all_read': 'Отметить всё прочитанным',
  'dashboard.notifications.empty': 'Уведомлений пока нет',
  'dashboard.time.just_now': 'только что',
  'dashboard.time.minutes_ago': '{count}м назад',
  'dashboard.time.hours_ago': '{count}ч назад',
  'dashboard.time.days_ago': '{count}д назад',
};
